import { createContext, useContext, useState, useMemo, useCallback, type ReactNode } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { marketingWorkFileApi } from "@/network/network";
import { showToast } from "@/other/utils/utils";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { shouldShowAsyncDialog } from "@/bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { MarketingTag, MarketingTagType } from "@/bryntum-lib/models/MarketingTag";
import { MarketingAsset } from "@/bryntum-lib/models/MarketingAsset";
import { ProblemSolutionTagMap } from "@/bryntum-lib/models/ProblemSolutionTagMap";
import { MarketingWorkFile } from "@/bryntum-lib/models/MarketingWorkFile";
import { MarketingWorkFileCard } from "@/bryntum-lib/models/MarketingWorkFileCard";
import {
  getBucketStatus,
  getHubStatus,
  getSequenceStatus,
} from "../constants";
import type { ProblemHub, BucketFill } from "../types";

export type CoverageFilter = "all" | "output_ready" | "partial" | "empty" | "sequence_ready";

export function hubMatchesCoverageFilter(hub: ProblemHub, filter: CoverageFilter): boolean {
  switch (filter) {
    case "all":
      return true;
    case "output_ready":
      return (
        (hub.overallStatus === "strong" || hub.overallStatus === "ready") &&
        (hub.sequenceStatus === "sequence_ready" || hub.sequenceStatus === "before_after_available")
      );
    case "partial":
      return hub.overallStatus === "partial";
    case "empty":
      return hub.overallStatus === "empty";
    case "sequence_ready":
      return hub.sequenceStatus === "sequence_ready" || hub.sequenceStatus === "before_after_available";
    default:
      return true;
  }
}

export interface KpiData {
  requiredFilled: number;
  requiredTotal: number;
  requiredPercent: number;
  outputReadyCount: number;
  sequenceReadyCount: number;
  blindSpotCount: number;
  hubsTotal: number;
}

export interface PromotedAssetSummary {
  id: number;
  caption: string;
  createdAt: Date;
  tagLabels: string[];
  problemHubLabels: string[];
}

export interface UserPromotionData {
  userId: number;
  name: string;
  profileImageURL?: string;
  promotions: number;
  promotedAssets: PromotedAssetSummary[];
}

export type PromotionDateRange = "7d" | "14d" | "1m" | "all";

const RANGE_DAYS: Record<PromotionDateRange, number | null> = {
  "7d": 7,
  "14d": 14,
  "1m": 30,
  all: null,
};

const RANGE_LABELS: Record<PromotionDateRange, string> = {
  "7d": "Last 7 days",
  "14d": "Last 14 days",
  "1m": "Last 30 days",
  all: "All time",
};

function getDateCutoff(range: PromotionDateRange, offsetPeriods = 0): Date | null {
  const days = RANGE_DAYS[range];
  if (days == null) return null;
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days * (1 + offsetPeriods));
  return d;
}

interface CoverageContextValue {
  problemHubs: ProblemHub[];
  filteredHubs: ProblemHub[];
  activeFilter: CoverageFilter;
  setActiveFilter: (filter: CoverageFilter) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedHub: ProblemHub | null;
  setSelectedHub: (hub: ProblemHub | null) => void;
  kpiData: KpiData;
  filterCounts: Record<CoverageFilter, number>;
  promotionsByUser: UserPromotionData[];
  promotionDateRange: PromotionDateRange;
  setPromotionDateRange: (range: PromotionDateRange) => void;
  promotionDateRangeLabel: string;
  totalPromotions: number;
  hubWorkFiles: MarketingWorkFile[];
  hubWorkfileCards: MarketingWorkFileCard[];
  handleGenerateWorkFile: (hub: ProblemHub, name: string) => Promise<void>;
}

const CoverageContext = createContext<CoverageContextValue | undefined>(undefined);

export const CoverageProvider = ({ children }: { children: ReactNode }) => {
  const { project, selectedBoard, editorHandler } = useSwivuBryntumContext();
  const slug = (selectedBoard?.record as any)?.slug as string | undefined;

  const { version: tagVersion } = useSwivuStore<MarketingTag>({ storeId: STORE_KEYS.marketingTagStore });
  const { version: assetVersion } = useSwivuStore<MarketingAsset>({ storeId: STORE_KEYS.marketingAssetStore });
  const { version: mapVersion } = useSwivuStore<ProblemSolutionTagMap>({ storeId: STORE_KEYS.problemSolutionTagMapStore });
  const { version: wfVersion } = useSwivuStore<MarketingWorkFile>({ storeId: STORE_KEYS.marketingWorkfileStore });
  const { version: wfCardVersion } = useSwivuStore<MarketingWorkFileCard>({ storeId: STORE_KEYS.marketingWorkfileCardStore });

  const [activeFilter, setActiveFilter] = useState<CoverageFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedHubId, setSelectedHubId] = useState<string | null>(null);
  const [promotionDateRange, setPromotionDateRange] = useState<PromotionDateRange>("1m");

  const problemHubs = useMemo<ProblemHub[]>(() => {
    if (!project) return [];

    const allTags = (project.marketingTagStore?.query(() => true, true) ?? []) as MarketingTag[];
    const allAssets = (project.marketingAssetStore?.query(() => true, true) ?? []) as MarketingAsset[];
    const allMaps = (project.problemSolutionTagMapStore?.query(() => true, true) ?? []) as ProblemSolutionTagMap[];

    const problemTags = allTags.filter((t) => t.type === MarketingTagType.PROBLEM);
    const contentTags = allTags.filter((t) => t.type === MarketingTagType.CONTENT);
    const temporalTags = allTags.filter((t) => t.type === MarketingTagType.TEMPORAL);

    const temporalIdsByLabel = new Map<string, number>();
    for (const t of temporalTags) {
      temporalIdsByLabel.set(t.label.toLowerCase(), t.id);
    }
    const beforeId = temporalIdsByLabel.get("before");
    const duringId = temporalIdsByLabel.get("during");
    const afterId = temporalIdsByLabel.get("after");

    const solutionIdsByProblem = new Map<number, Set<number>>();
    for (const m of allMaps) {
      let set = solutionIdsByProblem.get(m.problemTag);
      if (!set) {
        set = new Set();
        solutionIdsByProblem.set(m.problemTag, set);
      }
      set.add(m.solutionTag);
    }

    return problemTags.map((problemTag) => {
      const linkedSolutionIds = solutionIdsByProblem.get(problemTag.id) ?? new Set<number>();

      const hubAssets = allAssets.filter((a) =>
        a.tags?.some((tagId: number) => linkedSolutionIds.has(tagId)),
      );

      const buckets: BucketFill[] = contentTags.map((contentTag) => {
        const bucketAssets = hubAssets.filter((a) => a.tags?.includes(contentTag.id));
        const assetCount = bucketAssets.length;

        return {
          tagId: contentTag.id,
          label: contentTag.label,
          assetCount,
          status: getBucketStatus(assetCount),
          beforeCount: beforeId ? bucketAssets.filter((a) => a.tags?.includes(beforeId)).length : 0,
          duringCount: duringId ? bucketAssets.filter((a) => a.tags?.includes(duringId)).length : 0,
          afterCount: afterId ? bucketAssets.filter((a) => a.tags?.includes(afterId)).length : 0,
          assets: bucketAssets,
        };
      });

      const beforeAssets = beforeId ? hubAssets.filter((a) => a.tags?.includes(beforeId)) : [];
      const duringAssets = duringId ? hubAssets.filter((a) => a.tags?.includes(duringId)) : [];
      const afterAssets = afterId ? hubAssets.filter((a) => a.tags?.includes(afterId)) : [];

      const filledBuckets = buckets.filter((b) => b.assetCount > 0).length;

      return {
        id: String(problemTag.id),
        name: problemTag.label,
        description: problemTag.description,
        record: problemTag,
        buckets,
        sequenceStatus: getSequenceStatus(beforeAssets.length, duringAssets.length, afterAssets.length),
        filledRequired: filledBuckets,
        totalRequired: buckets.length,
        filledOptional: 0,
        totalOptional: 0,
        overallStatus: getHubStatus(buckets),
        temporalAssets: {
          before: beforeAssets,
          during: duringAssets,
          after: afterAssets,
        },
      };
    });
  }, [project, tagVersion, assetVersion, mapVersion]);

  const filteredHubs = useMemo(() => {
    let hubs = problemHubs.filter((h) => hubMatchesCoverageFilter(h, activeFilter));

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      hubs = hubs.filter((h) => h.name.toLowerCase().includes(q));
    }

    return hubs;
  }, [problemHubs, activeFilter, searchQuery]);

  const filterCounts = useMemo(() => {
    const filters: CoverageFilter[] = [
      "all",
      "output_ready",
      "partial",
      "empty",
      "sequence_ready",
    ];
    return filters.reduce(
      (acc, f) => {
        acc[f] = problemHubs.filter((h) => hubMatchesCoverageFilter(h, f)).length;
        return acc;
      },
      {} as Record<CoverageFilter, number>,
    );
  }, [problemHubs]);

  const kpiData = useMemo<KpiData>(() => {
    const requiredFilled = problemHubs.reduce((sum, h) => sum + h.filledRequired, 0);
    const requiredTotal = problemHubs.reduce((sum, h) => sum + h.totalRequired, 0);

    // Output-ready requires both content strength (strong or ready = 80%+ roles filled)
    // AND sequence capability (at least before + after assets exist).
    // A hub missing either dimension cannot produce a credible output.
    const outputReadyCount = problemHubs.filter(
      (h) =>
        (h.overallStatus === "strong" || h.overallStatus === "ready") &&
        (h.sequenceStatus === "sequence_ready" || h.sequenceStatus === "before_after_available"),
    ).length;

    // Sequence capable = before AND after assets exist (during optional for basic before/after outputs).
    const sequenceReadyCount = problemHubs.filter(
      (h) => h.sequenceStatus === "sequence_ready" || h.sequenceStatus === "before_after_available",
    ).length;

    const blindSpotCount = problemHubs.filter(
      (h) => h.buckets.every((b) => b.assetCount === 0),
    ).length;

    return {
      requiredFilled,
      requiredTotal,
      requiredPercent: requiredTotal > 0 ? Math.round((requiredFilled / requiredTotal) * 100) : 0,
      outputReadyCount,
      sequenceReadyCount,
      blindSpotCount,
      hubsTotal: problemHubs.length,
    };
  }, [problemHubs]);

  const promotionsByUser = useMemo<UserPromotionData[]>(() => {
    if (!project) return [];

    const allAssets = (project.marketingAssetStore?.query(() => true, true) ?? []) as MarketingAsset[];
    const allTags = (project.marketingTagStore?.query(() => true, true) ?? []) as MarketingTag[];
    const tagLabelMap = new Map<number, string>(allTags.map((t) => [t.id, t.label]));
    const problemTagLabelMap = new Map<number, string>(
      allTags
        .filter((t) => t.type === MarketingTagType.PROBLEM)
        .map((t) => [t.id, t.label])
    );
    const boardUsersStore = project.boardUsersStore;
    if (!boardUsersStore) return [];

    const cutoff = getDateCutoff(promotionDateRange);
    const filtered = cutoff
      ? allAssets.filter((a) => a.createdAt && new Date(a.createdAt) >= cutoff)
      : allAssets;

    const assetsByUser = new Map<number, PromotedAssetSummary[]>();
    for (const asset of filtered) {
      const uid = asset.creator;
      if (uid == null) continue;
      if (!assetsByUser.has(uid)) assetsByUser.set(uid, []);
      assetsByUser.get(uid)!.push({
        id: asset.id,
        caption: asset.caption ?? "",
        createdAt: new Date(asset.createdAt),
        problemHubLabels: (asset.tags ?? [])
          .map((tid: number) => problemTagLabelMap.get(tid))
          .filter((l): l is string => Boolean(l)),
        tagLabels: (asset.tags ?? [])
          .filter((tid: number) => !problemTagLabelMap.has(tid))
          .map((tid: number) => tagLabelMap.get(tid))
          .filter((l): l is string => Boolean(l))
          .slice(0, 4),
      });
    }

    // Sort each user's assets newest first
    for (const assets of assetsByUser.values()) {
      assets.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    }

    const result: UserPromotionData[] = [];
    for (const [userId, assets] of assetsByUser) {
      const boardUser = boardUsersStore.getById(userId) as any;
      if (boardUser) {
        result.push({
          userId,
          name: boardUser.name ?? `User ${userId}`,
          profileImageURL: boardUser.profileImageURL ?? undefined,
          promotions: assets.length,
          promotedAssets: assets,
        });
      }
    }

    result.sort((a, b) => b.promotions - a.promotions);

    if (result.length < 10) {
      const existingIds = new Set(result.map((r) => r.userId));
      const allBoardUsers = (boardUsersStore.query?.(() => true, true) ?? boardUsersStore.records ?? []) as any[];
      for (const bu of allBoardUsers) {
        if (result.length >= 10) break;
        const uid = bu.id ?? bu.data?.id;
        if (uid != null && !existingIds.has(uid)) {
          existingIds.add(uid);
          result.push({
            userId: uid,
            name: bu.name ?? `User ${uid}`,
            profileImageURL: bu.profileImageURL ?? undefined,
            promotions: 0,
            promotedAssets: [],
          });
        }
      }
    }

    return result;
  }, [project, assetVersion, tagVersion, promotionDateRange]);

  const totalPromotions = useMemo(
    () => promotionsByUser.reduce((sum, u) => sum + u.promotions, 0),
    [promotionsByUser],
  );

  const promotionDateRangeLabel = RANGE_LABELS[promotionDateRange];

  const hubWorkFiles = useMemo<MarketingWorkFile[]>(() => {
    if (!project || !selectedHubId) return [];
    const store = project.getCrudStore(STORE_KEYS.marketingWorkfileStore);
    if (!store) return [];
    const hubIdNum = Number(selectedHubId);
    return (store.query((r: MarketingWorkFile) => r.problemHubId === hubIdNum) as unknown as MarketingWorkFile[])
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [project, selectedHubId, wfVersion]);

  const hubWorkfileCards = useMemo<MarketingWorkFileCard[]>(() => {
    if (!project || hubWorkFiles.length === 0) return [];
    const store = project.getCrudStore(STORE_KEYS.marketingWorkfileCardStore);
    if (!store) return [];
    const wfIds = new Set(hubWorkFiles.map((f) => f.id));
    return store.query((r: MarketingWorkFileCard) => wfIds.has(r.workFile)) as unknown as MarketingWorkFileCard[];
  }, [project, hubWorkFiles, wfCardVersion]);

  const handleGenerateWorkFile = useCallback(async (hub: ProblemHub, name: string) => {
    if (!slug) {
      throw new Error("Board slug is not available");
    }
    try {
      await marketingWorkFileApi(slug).generate({
        problem_hub_id: Number(hub.id),
        name: name.trim(),
      });
      if (shouldShowAsyncDialog("MARKETING_WORK_FILE_GENERATION")) {
        (editorHandler as any).openEditor(
          EDITOR_KEYS.asyncTaskMessageEditor,
          null,
          { record: "MARKETING_WORK_FILE_GENERATION" },
          { useExisting: true, by: "recordType" },
        );
      }
    } catch {
      showToast({ msg: "Failed to start work file generation.", type: "error", duration: 3000 });
      throw new Error("Work file generation failed");
    }
  }, [slug, editorHandler]);

  const selectedHub = useMemo(
    () => problemHubs.find((h) => h.id === selectedHubId) ?? null,
    [problemHubs, selectedHubId],
  );

  const handleSetSelectedHub = useCallback((hub: ProblemHub | null) => {
    setSelectedHubId(hub?.id ?? null);
  }, []);

  const value: CoverageContextValue = {
    problemHubs,
    filteredHubs,
    activeFilter,
    setActiveFilter,
    searchQuery,
    setSearchQuery,
    selectedHub,
    setSelectedHub: handleSetSelectedHub,
    kpiData,
    filterCounts,
    promotionsByUser,
    promotionDateRange,
    setPromotionDateRange,
    promotionDateRangeLabel,
    totalPromotions,
    hubWorkFiles,
    hubWorkfileCards,
    handleGenerateWorkFile,
  };

  return <CoverageContext.Provider value={value}>{children}</CoverageContext.Provider>;
};

export const useCoverage = () => {
  const context = useContext(CoverageContext);
  if (!context) {
    throw new Error("useCoverage must be used within a CoverageProvider");
  }
  return context;
};
